import type { ReactNode } from "react";
import { createContext, useMemo } from "react";

import {
  type CreateToasterReturn,
  createToaster,
  useBreakpointValue,
} from "@chakra-ui/react";

import { Toaster } from "./ui/toaster";

type ToastContextValue = {
  toaster: CreateToasterReturn;
};

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const toasterTop = createToaster({
  pauseOnPageIdle: true,
  placement: "top",
});

const toasterBottomEnd = createToaster({
  pauseOnPageIdle: true,
  placement: "bottom-end",
});

type Placement =
  | "bottom"
  | "bottom-end"
  | "bottom-start"
  | "top"
  | "top-end"
  | "top-start";

type ToastProviderProps = {
  children?: ReactNode;
};

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const placement = useBreakpointValue<Placement>(
    {
      lg: "bottom-end",
      md: "top",
    },
    {
      fallback: "md",
    },
  );

  const toaster = useMemo(
    () => (placement === "bottom-end" ? toasterBottomEnd : toasterTop),
    [placement],
  );

  const value = useMemo(() => ({ toaster }), [toaster]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toaster={toasterTop} />
      <Toaster toaster={toasterBottomEnd} />
    </ToastContext.Provider>
  );
};

export { ToastContext };
